import { classnames, isSistaminuten } from '@/helpers';
import useMobileView from '@/hooks/useMobileView';
import { EmployeeStats, PlaceEmployee } from '@/types/state/shared';
import Avatar from '../Avatar';
import Label from '../Label/Label';
import Rating from '../redesign/Rating/Rating';
import Card from './Card';
import Icon from '@/components/icons/Icon';

import { useGetAmplitudeExperimentVariant } from '@/hooks/useAmplitudeExperiment';
import { NEW_PLACE_TWEAKS_EXPERIMENT } from '@/constants/experimentConstants';

type EmployeeCardProps = {
  employee: PlaceEmployee;
  stats?: EmployeeStats[string];
  readMore?: React.ReactNode;
  rightSlot?: React.ReactNode;
  onClick?: () => void;
};

const EmployeeCard = ({ rightSlot, employee, onClick, stats }: EmployeeCardProps) => {
  const getVariant = useGetAmplitudeExperimentVariant();
  const hasPlaceTweaksExperiment = getVariant(NEW_PLACE_TWEAKS_EXPERIMENT)?.value === 'on';

  const { isMobileView } = useMobileView();
  const { name, jobTitle, settings, info, certificates, avatar } = employee.about || {};
  const employeeJobTilte = (jobTitle || '').split('.').join('. ');
  const onlinePaymentForSistaminutenOnly = Boolean(settings?.onlinePaymentForSistaminutenOnly);
  const hideOnlinePayments = !isSistaminuten() && onlinePaymentForSistaminutenOnly;
  const acceptsKlarna = settings?.hasKlarna;
  const acceptsQliro = !hideOnlinePayments && settings?.useQliro;
  const hasCertificate = certificates?.length || false;
  const acceptsGiftcard = !isSistaminuten() && settings?.acceptsGiftCard;
  const hasBadges = acceptsGiftcard || acceptsKlarna || acceptsQliro || hasCertificate;

  return (
    <>
      <Card size="sm">
        <div data-cy="employee" className={`gap-sm flex ${isMobileView ? 'flex-col' : ''}`}>
          <button
            onClick={onClick}
            disabled={!onClick}
            className={`flex flex-1 items-center justify-between text-start outline-none`}>
            <div className="gap-sm flex flex-1 items-start overflow-hidden">
              <div className="gap-xs flex w-full flex-col">
                <div className="flex justify-between">
                  <div className="gap-sm flex w-full">
                    <Avatar src={settings?.showPhoto === false ? null : avatar} size={'md'} />
                    <div className="gap-xs flex w-full flex-col justify-center overflow-hidden">
                      <div className="flex w-full items-center justify-between">
                        <div className="gap-2xs flex flex-col">
                          <h2 className="text-l text-fg_primary">{name}</h2>
                          <div className="text-s gap-2xs flex items-start">
                            <div className={`text-fg_secondary flex w-full items-start`}>
                              {Boolean(employeeJobTilte) && employeeJobTilte}
                            </div>
                          </div>
                        </div>
                        {hasPlaceTweaksExperiment && (
                          <div className={`text-s text-fg_link flex flex-shrink-0 ${isMobileView ? '' : 'items-end'}`}>
                            <Icon variant="info-circle"  style={{ width: '20px', height: '20px' }} color="fg_link" />
                          </div>
                        )}
                        {hasBadges && !isMobileView && !hasPlaceTweaksExperiment && (
                          <div className="space-x-sm flex flex-shrink-0 items-center">
                            {acceptsKlarna && <Label variant="klarna" />}
                            {acceptsQliro && <Label variant="qliro" icon={{ variant: 'qliro' }} />}
                            {hasCertificate && (
                              <a
                                href="#certificates"
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}>
                                <Label variant="certificate" />
                              </a>
                            )}
                            {acceptsGiftcard && <Label variant="giftcard" />}
                          </div>
                        )}
                      </div>

                      {!isMobileView && info && !hasPlaceTweaksExperiment && (
                        <span
                          className="text-s text-fg_secondary flex items-start"
                          style={{
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            WebkitLineClamp: 2,
                            textOverflow: 'ellipsis',
                          }}>
                          {info}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {isMobileView && info && !hasPlaceTweaksExperiment && (
                  <span
                    className="text-s text-fg_secondary flex items-start"
                    style={{
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      WebkitLineClamp: 2,
                      textOverflow: 'ellipsis',
                    }}>
                    {info}
                  </span>
                )}
                {hasBadges && isMobileView && !hasPlaceTweaksExperiment && (
                  <div className="space-x-sm flex items-center">
                    {acceptsKlarna && <Label variant="klarna" />}
                    {acceptsQliro && <Label variant="qliro" icon={{ variant: 'qliro' }} />}
                    {hasCertificate && (
                      <a
                        href="#certificates"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}>
                        <Label variant="certificate" />
                      </a>
                    )}

                    {acceptsGiftcard && <Label variant="giftcard" />}
                  </div>
                )}
              </div>
            </div>
          </button>
          <div className={`border-border_regular ${isMobileView ? 'border-t' : 'border-l'}`}>
            <div
              className={classnames(
                'text-s gap-sm flex h-full items-center',
                isMobileView ? `pt-sm ${Boolean(stats?.count) ? 'justify-between' : 'justify-end'}` : 'pl-sm py-xs',
                hasPlaceTweaksExperiment && 'justify-end min-w-[170px]'
              )}>
              {Boolean(stats?.count) && (
                <div className={`flex ${isMobileView ? 'gap-xs items-center' : 'gap-2xs flex-col items-end'}`}>
                  <Rating
                    ratingCount={stats.count}
                    avgRating={stats?.score ?? 0}
                    size={'md'}
                    showOneStar={true}
                    onReviewsClick={() => {}}
                  />
                  <span
                    className={`flex ${
                      isMobileView
                        ? 'before:w-xxs before:h-xxs before:mr-xs before:bg-fg_secondary before:mt-[5px] before:inline-block before:rounded-full before:content-[""]'
                        : ''
                    } text-fg_secondary`}>
                    {stats?.count} betyg
                  </span>
                </div>
              )}
              {rightSlot && rightSlot}
            </div>
          </div>
        </div>
      </Card>
    </>
  );
};

export default EmployeeCard;
